import { apiClient } from './client'

export type HsiZeroPlanMode = 'TreatAsPerfect' | 'IgnoreInScore' | 'UseEpsilon' | 0 | 1 | 2

export type HsiWeightsDto = {
  distanceWeight: number
  elevationWeight: number
  packWeight: number
}

export type HsiClampingSettingsDto = {
  indexMin: number
  indexMax: number
  hsiPercentMin: number
  hsiPercentMax: number
  epsilon: number
}

export type HsiBandDto = {
  id: string
  minPercent: number
  maxPercent?: number | null
  label: string
  color?: string | null
  description?: string | null
}

export type HsiMetricBehaviorSettingsDto = {
  distanceZeroPlanMode: HsiZeroPlanMode
  elevationZeroPlanMode: HsiZeroPlanMode
  packZeroPlanMode: HsiZeroPlanMode
}

export type HikeStrongIndexConfigDto = {
  id?: string
  version: string
  isActive: boolean
  name: string
  weights: HsiWeightsDto
  clamping: HsiClampingSettingsDto
  bands: HsiBandDto[]
  metricBehavior: HsiMetricBehaviorSettingsDto
  createdAt?: string | null
  updatedAt?: string | null
  notes?: string | null
}

export type TestHikeStrongIndexRequest = {
  planId: string
  asOfUtc?: string
  configOverride?: HikeStrongIndexConfigDto
}

export type HikeStrongIndexResultDto = {
  distanceIndex: number
  elevationIndex: number
  packIndex: number
  scorePercent: number
  band: string
  periodStart: string
  periodEnd: string
}

export type TestHikeStrongIndexResponse = {
  success: boolean
  errorMessage?: string | null
  result?: HikeStrongIndexResultDto
  validationErrors?: string[]
  validationWarnings?: string[]
}

export async function getActiveHsiConfig(): Promise<HikeStrongIndexConfigDto> {
  const { data } = await apiClient.get<HikeStrongIndexConfigDto>('/api/admin/hike-strong-index/config')
  return data
}

export async function getAllHsiConfigs(): Promise<HikeStrongIndexConfigDto[]> {
  const { data } = await apiClient.get<HikeStrongIndexConfigDto[]>('/api/admin/hike-strong-index/configs')
  return data
}

export async function getDefaultHsiConfig(): Promise<HikeStrongIndexConfigDto> {
  const { data } = await apiClient.get<HikeStrongIndexConfigDto>('/api/admin/hike-strong-index/config/defaults')
  return data
}

export async function createHsiConfig(config: HikeStrongIndexConfigDto): Promise<HikeStrongIndexConfigDto> {
  const { data } = await apiClient.post<HikeStrongIndexConfigDto>('/api/admin/hike-strong-index/config', config)
  return data
}

export async function updateHsiConfig(id: string, config: HikeStrongIndexConfigDto): Promise<HikeStrongIndexConfigDto> {
  const { data } = await apiClient.put<HikeStrongIndexConfigDto>(`/api/admin/hike-strong-index/config/${id}`, config)
  return data
}

export async function activateHsiConfig(id: string): Promise<HikeStrongIndexConfigDto> {
  const { data } = await apiClient.post<HikeStrongIndexConfigDto>(`/api/admin/hike-strong-index/config/${id}/activate`)
  return data
}

export async function deleteHsiConfig(id: string): Promise<void> {
  await apiClient.delete(`/api/admin/hike-strong-index/config/${id}`)
}

export async function validateHsiConfig(config: HikeStrongIndexConfigDto): Promise<{
  isValid: boolean
  errors: string[]
  warnings: string[]
}> {
  const { data } = await apiClient.post('/api/admin/hike-strong-index/config/validate', config)
  return data
}

export async function testHsi(request: TestHikeStrongIndexRequest): Promise<TestHikeStrongIndexResponse> {
  const { data } = await apiClient.post<TestHikeStrongIndexResponse>('/api/admin/hike-strong-index/test', request)
  return data
}

